import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';
import { calculateDepositStatus } from '../../services/texasComplianceService';

export const DepositTracker: React.FC = () => {
  const [deposits, setDeposits] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await texasApi.deposits.getAll();
      if (Array.isArray(res)) {
        setDeposits(res);
      } else {
        const d = res?.data;
        setDeposits(Array.isArray(d) ? d : Array.isArray(d?.deposits) ? d.deposits : []);
      }
    } catch (err: any) {
      try {
        const tenantRes = await landlordApi.get('api/tenants');
        const tenants = tenantRes?.data?.data || [];
        const items: any[] = [];
        tenants.forEach((t: any) => {
          if (t.depositAmount && (t.tenancyEndDate || t.surrenderDate)) {
            const surrender = t.surrenderDate || t.tenancyEndDate;
            const calc = calculateDepositStatus(surrender);
            items.push({
              id: 'dep-' + (t.id || t.tenantId),
              tenantId: t.id || t.tenantId,
              tenantName: t.name || (t.firstName + ' ' + (t.lastName || '')).trim() || 'Tenant',
              propertyId: t.propertyId || 'prop-default',
              propertyName: t.propertyName || 'Texas Property',
              unitNumber: t.unitNumber || t.unit,
              depositAmount: Number(t.depositAmount) || 0,
              depositReference: t.depositReference || ('DEP-' + t.id),
              surrenderDate: surrender,
              deadlineDate: calc.deadlineDate,
              daysRemaining: calc.daysRemaining,
              status: calc.status,
              refundStatus: t.refundStatus || 'pending',
            });
          }
        });
        setDeposits(items);
      } catch {
        setError('Failed to load deposit tracker');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = filterStatus === 'all' ? deposits : deposits.filter((d: any) => d.status === filterStatus);
  const overdue = deposits.filter((d: any) => d.status === 'overdue').length;
  const critical = deposits.filter((d: any) => d.status === 'critical').length;
  const totalDeposits = deposits.reduce((sum: number, d: any) => sum + (d.depositAmount || 0), 0);

  const handleRecordSurrender = async (tenancyId: string) => {
    try {
      await texasApi.deposits.recordSurrender(tenancyId);
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to record surrender');
    }
  };

  const handleRecordReturn = async (tenancyId: string) => {
    try {
      await texasApi.deposits.recordReturn(tenancyId);
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to record return');
    }
  };

  return (
    <div data-testid="deposit-tracker" className="texas-page-container">
      <h1>Deposit Tracker</h1>
      <p className="page-description">
        Security deposit countdowns — track surrender deadlines and refund statuses.
      </p>

      {error && <div className="error" data-testid="error">{error}</div>}

      {!loading && (
        <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-slate-500 tracking-wider">TOTAL DEPOSITS</div>
            <div className="text-3xl font-black text-slate-900 mt-1">{deposits.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-red-600 tracking-wider">OVERDUE</div>
            <div className="text-3xl font-black text-red-600 mt-1">{overdue}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-amber-600 tracking-wider">CRITICAL</div>
            <div className="text-3xl font-black text-amber-600 mt-1">{critical}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-emerald-600 tracking-wider">TOTAL VALUE</div>
            <div className="text-3xl font-black text-emerald-600 mt-1">${totalDeposits.toLocaleString()}</div>
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded text-xs ${filterStatus === 'all' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-slate-600'}`}
            data-testid="filter-all"
          >All</button>
          <button
            onClick={() => setFilterStatus('overdue')}
            className={`px-3 py-1 rounded text-xs ${filterStatus === 'overdue' ? 'bg-red-600 text-white' : 'bg-gray-100 text-slate-600'}`}
            data-testid="filter-overdue"
          >Overdue</button>
          <button
            onClick={() => setFilterStatus('critical')}
            className={`px-3 py-1 rounded text-xs ${filterStatus === 'critical' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-slate-600'}`}
            data-testid="filter-critical"
          >Critical</button>
          <button
            onClick={() => setFilterStatus('warning')}
            className={`px-3 py-1 rounded text-xs ${filterStatus === 'warning' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-slate-600'}`}
            data-testid="filter-warning"
          >Warning</button>
          <button
            onClick={() => setFilterStatus('normal')}
            className={`px-3 py-1 rounded text-xs ${filterStatus === 'normal' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-slate-600'}`}
            data-testid="filter-normal"
          >Normal</button>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Deposit Items ({filtered.length})</h2>
        {loading ? (
          <div className="no-results">Loading deposits...</div>
        ) : filtered.length === 0 ? (
          <div className="no-results">No deposit items found</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((dep: any) => (
              <div key={dep.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80 flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-900 text-xs">{dep.tenantName} — {dep.propertyName}</div>
                  <div className="text-xs text-slate-500">
                    ${(dep.depositAmount || 0).toLocaleString()} · {dep.unitNumber || ''} · {dep.daysRemaining}d remaining · {dep.refundStatus || 'pending'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    dep.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    dep.status === 'critical' ? 'bg-orange-100 text-orange-700' :
                    dep.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {dep.status || 'normal'}
                  </span>
                  <button
                    onClick={() => handleRecordSurrender(dep.tenantId)}
                    className="px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] hover:bg-red-200"
                    data-testid={`surrender-${dep.id}`}
                  >Surrender</button>
                  <button
                    onClick={() => handleRecordReturn(dep.tenantId)}
                    className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-[10px] hover:bg-emerald-200"
                    data-testid={`return-${dep.id}`}
                  >Return</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DepositTracker;
